import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw, ExternalLink, FileImage, Download, LayoutGrid, Rows, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface Row {
  id: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email: string;
  mobile_country_code: string;
  mobile_number: string;
  region: string;
  city: string;
  category: string | null;
  payment_choice: string | null;
  payment_status: string | null;
  zelle_memo: string | null;
  payment_screenshot_path: string | null;
  created_at: string;
  updated_at: string | null;
}

interface RegionLookup {
  name: string | null;
}

interface Props {
  isAuthorized: boolean;
  userRole?: string;
  userRegionId?: string | null;
  selectedRegion?: string;
}

const regionBase = (v?: string | null) =>
  (v || "").toLowerCase().replace(/\s*region\s*$/i, "").trim();

export const PaymentConfirmationsPanel = ({ isAuthorized, userRole, userRegionId, selectedRegion }: Props) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [regionName, setRegionName] = useState<string | null>(null);
  const [regionOptions, setRegionOptions] = useState<string[]>([]);
  const [pickedRegion, setPickedRegion] = useState<string>("all");
  const [view, setView] = useState<"table" | "cards">("table");
  const [onlyWithScreenshot, setOnlyWithScreenshot] = useState(false);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const canDelete = userRole === "global_admin" || userRole === "president";
  const isUnscoped = userRole === "global_admin" || userRole === "president";
  const isRegional = !isUnscoped && !!userRegionId;
  const activeRegion = selectedRegion ?? pickedRegion;

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      if (deleting.payment_screenshot_path) {
        await supabase.storage.from("conference-payments").remove([deleting.payment_screenshot_path]);
      }
      const { error } = await supabase.from("conference_registrations").delete().eq("id", deleting.id);
      if (error) throw error;
      setRows((prev) => prev.filter((r) => r.id !== deleting.id));
      toast({ title: "Registration deleted", description: `${deleting.first_name} ${deleting.last_name} removed from database.` });
      setDeleting(null);
    } catch (err: unknown) {
      toast({ title: "Delete failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized || !isUnscoped) return;
    supabase
      .from("regions")
      .select("name")
      .order("name")
      .then(({ data }) => setRegionOptions(((data || []) as RegionLookup[]).map((r) => r.name || "").filter(Boolean)));
  }, [isAuthorized, isUnscoped]);

  const load = async () => {
    setLoading(true);
    try {
      let regionFilter: string | null = null;
      if (isRegional && userRegionId) {
        const { data: reg } = await supabase
          .from("regions")
          .select("name")
          .eq("id", userRegionId)
          .maybeSingle<RegionLookup>();
        regionFilter = reg?.name || null;
        setRegionName(regionFilter);
      } else if (isUnscoped && activeRegion !== "all") {
        regionFilter = activeRegion;
      }

      let q = supabase
        .from("conference_registrations")
        .select(
          "id, first_name, middle_name, last_name, email, mobile_country_code, mobile_number, region, city, category, payment_choice, payment_status, zelle_memo, payment_screenshot_path, created_at, updated_at"
        )
        .order("updated_at", { ascending: false });

      if (onlyWithScreenshot) q = q.not("payment_screenshot_path", "is", null);
      if (regionFilter) q = q.ilike("region", `%${regionBase(regionFilter)}%`);

      const { data, error } = await q;
      if (error) throw error;
      const nextRows = ((data || []) as Row[]).filter(
        (r) => !regionFilter || regionBase(r.region) === regionBase(regionFilter)
      );
      setRows(nextRows);
      const nextUrls = Object.fromEntries(
        (
          await Promise.all(
            nextRows.map(async (row) => {
              if (!row.payment_screenshot_path) return [row.id, ""] as const;
              const { data: signed } = await supabase.storage
                .from("conference-payments")
                .createSignedUrl(row.payment_screenshot_path, 60 * 10);
              return [row.id, signed?.signedUrl || ""] as const;
            })
          )
        ).filter((entry) => entry[1])
      );
      setSignedUrls(nextUrls);
    } catch (err: unknown) {
      toast({ title: "Failed to load payment confirmations", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, userRegionId, userRole, activeRegion, onlyWithScreenshot]);


  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.id, r.first_name, r.last_name, r.middle_name, r.email, r.region, r.city, r.zelle_memo]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const openScreenshot = async (path: string | null) => {
    if (!path) return;
    const { data, error } = await supabase.storage
      .from("conference-payments")
      .createSignedUrl(path, 60 * 10);
    if (error || !data?.signedUrl) {
      toast({ title: "Unable to open screenshot", description: error?.message || "No URL available", variant: "destructive" });
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  if (!isAuthorized) return null;

  const statusBadge = (s: string | null) => {
    if (s === "confirmed") return <Badge className="bg-emerald-600">Paid</Badge>;
    return <Badge variant="secondary">Not Paid</Badge>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileImage className="w-5 h-5" /> Payment Confirmations
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Payment records
            {isRegional && regionName
              ? ` for ${regionName} region`
              : isUnscoped && activeRegion !== "all"
                ? ` for ${activeRegion} region`
                : " across all regions"} — {rows.length} shown
            {onlyWithScreenshot ? " (screenshots only)" : ""}
          </p>

        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isUnscoped && selectedRegion === undefined && (
            <Select value={pickedRegion} onValueChange={setPickedRegion}>
              <SelectTrigger className="w-[190px] h-9">
                <SelectValue placeholder="All regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {regionOptions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            placeholder="Search reg ID, name, email, memo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <Button
            variant={onlyWithScreenshot ? "default" : "outline"}
            size="sm"
            onClick={() => setOnlyWithScreenshot((v) => !v)}
          >
            <FileImage className="w-4 h-4 mr-1" /> Screenshots only
          </Button>


          <div className="inline-flex rounded-md border bg-background overflow-hidden">
            <Button
              type="button"
              size="sm"
              variant={view === "table" ? "default" : "ghost"}
              className="rounded-none"
              onClick={() => setView("table")}
            >
              <Rows className="w-4 h-4 mr-1" /> Table
            </Button>
            <Button
              type="button"
              size="sm"
              variant={view === "cards" ? "default" : "ghost"}
              className="rounded-none"
              onClick={() => setView("cards")}
            >
              <LayoutGrid className="w-4 h-4 mr-1" /> Cards
            </Button>
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            No payment records{isRegional && regionName
              ? ` for ${regionName} region`
              : isUnscoped && activeRegion !== "all"
                ? ` for ${activeRegion} region`
                : ""}
            {onlyWithScreenshot ? " with an uploaded screenshot." : " yet."}
          </div>

        ) : view === "table" ? (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Thumbnail</TableHead>
                  <TableHead>First Name</TableHead>
                  <TableHead>Last Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="font-mono text-xs">Registration ID</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {signedUrls[r.id] ? (
                        <button
                          type="button"
                          onClick={() => openScreenshot(r.payment_screenshot_path)}
                          className="block overflow-hidden rounded-md border bg-muted"
                          title="Click to open full screenshot"
                        >
                          <img
                            src={signedUrls[r.id]}
                            alt={`Payment screenshot for ${r.first_name} ${r.last_name}`}
                            loading="lazy"
                            className="h-16 w-24 object-cover"
                          />
                        </button>
                      ) : (
                        <div className="h-16 w-24 rounded-md border bg-muted flex items-center justify-center text-xs text-muted-foreground">
                          —
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{r.first_name}</TableCell>
                    <TableCell className="font-medium">{r.last_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.email}</TableCell>
                    <TableCell className="font-mono text-xs">{r.id}</TableCell>
                    <TableCell className="text-sm">{r.region}{r.city ? `, ${r.city}` : ""}</TableCell>
                    <TableCell>{statusBadge(r.payment_status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(r.updated_at || r.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openScreenshot(r.payment_screenshot_path)}>
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openScreenshot(r.payment_screenshot_path)}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleting(r)}
                            title="Delete registration"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((r) => (
              <div key={r.id} className="border rounded-lg p-3 bg-card hover:shadow-sm transition">
                {signedUrls[r.id] ? (
                  <button
                    type="button"
                    onClick={() => openScreenshot(r.payment_screenshot_path)}
                    className="mb-3 block w-full overflow-hidden rounded-md border bg-muted"
                  >
                    <img
                      src={signedUrls[r.id]}
                      alt={`Payment confirmation for ${[r.first_name, r.last_name].filter(Boolean).join(" ")} (${r.email})`}
                      loading="lazy"
                      className="h-44 w-full object-cover"
                    />
                  </button>
                ) : null}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">
                      {[r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ")}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.mobile_country_code} {r.mobile_number}
                    </div>
                  </div>
                  {statusBadge(r.payment_status)}
                </div>
                <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                  <div>
                    <span className="font-medium text-foreground">Reg ID:</span> {r.id}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Region:</span> {r.region}
                    {r.city ? `, ${r.city}` : ""}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Category:</span> {r.category || "—"} ·{" "}
                    <span className="font-medium text-foreground">Method:</span> {r.payment_choice || "—"}
                  </div>
                  {r.zelle_memo && (
                    <div className="truncate font-mono bg-muted px-1.5 py-0.5 rounded mt-1">
                      {r.zelle_memo}
                    </div>
                  )}
                  <div>
                    Uploaded: {new Date(r.updated_at || r.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openScreenshot(r.payment_screenshot_path)}>
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openScreenshot(r.payment_screenshot_path)}>
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                      onClick={() => setDeleting(r)}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </CardContent>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteBusy && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this registration?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  This permanently removes the conference registration
                  {deleting?.payment_screenshot_path ? " and its uploaded payment screenshot" : ""}
                  {" "}from the database. It will disappear from both the Payment Confirmations panel and the Registrations admin list.
                </div>
                {deleting && (
                  <div className="rounded-md border bg-muted/50 p-2 text-xs space-y-1">
                    <div><strong>{deleting.first_name} {deleting.last_name}</strong> — {deleting.email}</div>
                    <div className="text-muted-foreground">{deleting.region}{deleting.city ? `, ${deleting.city}` : ""}</div>
                    <div className="font-mono text-muted-foreground">Reg ID: {deleting.id}</div>
                  </div>
                )}
                <div className="text-destructive font-medium">This action cannot be undone.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteBusy}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default PaymentConfirmationsPanel;
